/* eslint-disable react/no-did-update-set-state */
/* eslint-disable no-unused-vars */

import React, { useState, useEffect } from 'react'
import { node } from 'prop-types'

import { trackEvent, getCheckoutHash } from 'utils/tracking'
import {
  getCheckout,
  createCheckoutMutation,
  checkoutLineItemsAdd,
  checkoutLineItemsUpdate,
  checkoutLineItemsRemove,
  checkoutDiscountCodeApply,
  checkoutDiscountCodeRemove,
  checkoutUpdateCustomAttribute
} from 'queries/checkout'
import Toast from 'components/Toast'
import useDigitalData from 'hooks/useDigitalData'
import { getVariantAttributes } from 'utils/tracking'

const Context = React.createContext({
  loading: true
})

const CheckoutProvider = ({ children }) => {
  const [checkout, setCheckout] = useState(null)
  const [loading, setLoading] = useState(true)
  const [checkoutUpToDate, setCheckoutUpToDate] = useState(true)
  const [toastVisible, setToastVisible] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [discount, setDiscount] = useState(null)
  const [authSdk, setAuthSdk] = useState(null)
  const [user, setUser] = useState(null)
  const [priceBreakdown, setPriceBreakdown] = useState(null)
  const [priceBreakdownLoading, setPriceBreakdownLoading] = useState(false)
  const [tinyCartVisible, setTinyCartVisible] = useState(false)
  const [regularLineItems, setRegularLineItems] = useState(null)
  const [subscriptionLineItems, setSubscriptionLineItems] = useState(null)
  const [regularLineItemsQuantity, setRegularLineItemsQuantity] = useState(null)
  const [subscriptionLineItemsQuantity, setSubscriptionLineItemsQuantity] = useState(null)
  const [applyDiscountCodeError, setApplyDiscountCodeError] = useState(null)
  const [applyCreditError, setApplyCreditError] = useState(null)
  const [products, setProducts] = useState([])
  const [draftOrder, setDraftOrder] = useState({})
  const [skipSubscription, setSkipsubscription] = useState(false)
  const [queuedOrder, setQueuedOrder] = useState(null)
  const [hasSeenChristmasPopup, setHasSeenChristmasPopup] = useState(false)
  const [noAutoDiscount, setNoAutoDiscount] = useState(false)
  const [selectedShippingMethod, setSelectedShippingMethod] = useState(null)
  const [selectedSubscriptionShippingMethod, setSelectedSubscriptionShippingMethod] = useState(null)

  const { digitalData } = useDigitalData()

  useEffect(() => {
    try {
      if (!checkout || !regularLineItems || !subscriptionLineItems || !priceBreakdown) return

      const discount = priceBreakdown?.regular?.discount

      const combinedLineItems = [
        ...subscriptionLineItems.map(li => ({ ...li, isSubscription: true })),
        ...regularLineItems.map(li => ({ ...li, isSubscription: false }))
      ]

      const subtotal = (
        parseFloat(priceBreakdown?.regular?.lineItemsSubtotalPrice || '0.00') +
        parseFloat(priceBreakdown?.subscription?.lineItemsSubtotalPrice || '0.00')
      ).toFixed(2)

      const total = (
        parseFloat(priceBreakdown?.regular?.subtotalPrice || '0.00') +
        parseFloat(priceBreakdown?.subscription?.subtotalPrice || '0.00')
      ).toFixed(2)

      const newCart = {
        cartId: checkout.id,
        price: {
          subtotal,
          cartTotal: total,
          coupon: discount
            ? {
                couponCode: discount.code,
                couponDiscount: discount.value
              }
            : null
        },
        productInfo: combinedLineItems.map(({ node: li, isSubscription }) => ({
          productId: li.variant.id,
          productName: li.title,
          price: li.variant.price,
          quantity: li.quantity,
          productSku: li.variant.sku,
          attributes: {
            orderType: isSubscription ? 'Subscription' : 'One-Time',
            ...li.variant.selectedOptions.reduce(
              (acc, { name, value }) => ({ ...acc, [name.toLowerCase()]: value }),
              {}
            )
          }
        }))
      }
      // Prevent sending an event if the cart hasn't really changed
      if (window.digitalData.cart && getCheckoutHash(newCart) === getCheckoutHash(window.digitalData.cart)) {
        return
      }
      digitalData.push({
        cart: newCart
      })
    } catch (e) {
      console.log(e)
    }
  }, [checkout, regularLineItems, subscriptionLineItems, priceBreakdown])

  useEffect(() => {
    if (!process.browser) return

    const localStorageCheckoutId = localStorage.getItem('checkoutId')

    if (localStorageCheckoutId) {
      getCurrentCart(localStorageCheckoutId)
    } else {
      if (!checkout) {
        createCheckout()
      }
    }
  }, [])

  useEffect(() => {
    if (authSdk) {
      fetchProducts()
    }
  }, [authSdk])

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search.slice(1))
    const queryParams = Array.from(qs.entries()).reduce((acc, [k, v]) => ({ ...acc, [k]: v }), {})
    const discount = queryParams.discount

    setDiscount(discount)
  }, [discount])

  useEffect(() => {
    if (authSdk) {
      dispatchPriceBreakdown()
      splitCheckoutLineItems()
      dispatchDraftOrder()
    }
  }, [checkout])

  useEffect(() => {
    if (authSdk && checkout) {
      dispatchPriceBreakdown()
    }
  }, [user])

  useEffect(() => {
    if (authSdk) {
      dispatchPriceBreakdown()
    }
  }, [skipSubscription])

  const fetchProducts = async () => {
    const { products } = await authSdk.getProducts()
    setProducts(products)
  }

  const dispatchDraftOrder = () => {
    if (checkout) {
      const subscriptionLineItems = checkout.lineItems.edges.filter(item =>
        item.node.customAttributes.find(a => a.key === 'orderType' && a.value === 'subscription')
      )

      setDraftOrder({
        ...draftOrder,
        [checkout.id]: subscriptionLineItems
      })
    }
  }

  const splitCheckoutLineItems = () => {
    if (checkout) {
      const { edges } = checkout.lineItems

      const subscriptionLineItems = edges.filter(p =>
        p.node.customAttributes.find(a => a.key === 'orderType' && a.value === 'subscription')
      )

      const regularLineItems = edges.filter(p => !p.node.customAttributes.find(a => a.key === 'orderType'))

      const regularLineItemsQuantity = regularLineItems
        .map(p => p.node.quantity)
        .reduce((total, currentValue) => total + currentValue, 0)

      const subscriptionLineItemsQuantity = subscriptionLineItems
        .map(p => p.node.quantity)
        .reduce((total, currentValue) => total + currentValue, 0)

      setSubscriptionLineItems(subscriptionLineItems)
      setRegularLineItems(regularLineItems)
      setRegularLineItemsQuantity(regularLineItemsQuantity)
      setSubscriptionLineItemsQuantity(subscriptionLineItemsQuantity)
    }
  }

  const setCheckoutUser = user => {
    setUser(user)
  }

  const dispatchPriceBreakdown = async (shippingMethodPrice = null) => {
    setPriceBreakdownLoading(true)

    const body = {
      checkout,
      tierLevel: user?.currentTierLevel,
      province: user?.province,
      shippingMethodPrice: selectedShippingMethod?.total_price,
      skipSubscription,
      noAutoDiscount
    }

    const { priceBreakdown } = await authSdk.getPriceBreakdown({
      params: body,
      token: localStorage.token || null
    })

    const discount = priceBreakdown?.regular?.discount
    if (discount) {
      await removeCredit()
    }

    setPriceBreakdown(priceBreakdown)
    setPriceBreakdownLoading(false)
  }

  const createCheckout = async ({ customAttributes = {} } = {}, currentCart = true) => {
    let input = {}
    const localStorageToken = process.browser ? localStorage.getItem('token') : null

    if (localStorageToken) {
      input = {
        ...input,
        customAttributes: [
          {
            key: 'jwt',
            value: localStorageToken
          },
          ...Object.entries(customAttributes).map(([key, value]) => ({
            key,
            value
          }))
        ]
      }
    }

    const responseCheckout = await createCheckoutMutation({ input })
    setCheckout(responseCheckout)
    if (process.browser && currentCart) localStorage.setItem('checkoutId', responseCheckout.id)

    window.dataObject.cartProduct = responseCheckout.lineItems.edges.map(e => ({
      quantity: e.node.quantity,
      variantId: e.node.variant.id,
      price: e.node.variant.price,
      name: e.node.title
    }))
    setLoading(false)
  }

  const getCurrentCart = async checkoutId => {
    setLoading(true)

    try {
      const responseCheckout = await getCheckout({ checkoutId })

      window.dataObject.cartProduct = responseCheckout.lineItems.edges.map(e => ({
        quantity: e.node.quantity,
        variantId: e.node.variant.id,
        price: e.node.variant.price,
        name: e.node.title
      }))

      if (!responseCheckout || responseCheckout.lineItems.edges.filter(item => item.node.variant === null).length) {
        createCheckout()
      } else {
        setCheckout(responseCheckout)
        setLoading(loading)
      }
    } catch (e) {
      localStorage.removeItem('checkoutId')
      createCheckout()
    }

    setLoading(false)
  }

  const addLineItemsToCart = async (lineItems, { silent } = { silent: false }) => {
    setLoading(true)
    const formattedLineItems = lineItems.map(l => {
      const customAttributes = l.customAttributes || []

      return {
        quantity: l.quantity,
        variantId: l.variantId,
        customAttributes: [
          ...customAttributes,
          {
            key: 'jwt',
            value: localStorage.token || ''
          }
        ]
      }
    })
    console.log({ checkout, lineItems })
    const responseCheckout = await checkoutLineItemsAdd({
      checkout,
      lineItems: formattedLineItems
    })

    showToast('Added to cart')
    setCheckout(responseCheckout)
    setLoading(false)

    try {
      trackEvent({
        name: 'cartProdAdd',
        time: Date(),
        product: lineItems
      })

      window.dataLayer.push({
        event: 'addToCart',
        ecommerce: {
          currencyCode: 'CAN',
          add: {
            products: lineItems.map(li => ({
              name: li.product?.title,
              id: li.variant.sku,
              price: li.variant.price,
              brand: 'vype',
              category: '',
              variant: li.variant.title,
              quantity: li.quantity
            }))
          }
        }
      })

      window.dataObject.cartProduct = responseCheckout.lineItems.edges.map(e => ({
        quantity: e.node.quantity,
        variantId: e.node.variant.id,
        price: e.node.variant.price,
        name: e.node?.title + ' ' + e.node.variant.name
      }))

      window._satellite.track('scAdd', {
        productInfo: lineItems.map(li => {
          const orderType = li.customAttributes?.some(attr => attr.key === 'orderType' && attr.value === 'subscription')
            ? 'Subscription'
            : 'One-Time'
          const attributes = getVariantAttributes({ variant: li.variant, orderType })
          const splitProductId = li.variant.id.split('__')
          const productId = splitProductId[splitProductId.length - 1]
          const productName = responseCheckout.lineItems.edges.find(e => e.node.variant.sku === li.variant.sku).node
            .title

          return {
            productId,
            productSku: li.variant.sku,
            productName,
            price: li.variant.price,
            quantity: li.quantity,
            attributes
          }
        })
      })
    } catch (e) {
      console.log(e)
    }

    setLoading(false)
  }

  const updateLineItemsInCart = async lineItems => {
    setLoading(true)

    const responseCheckout = await checkoutLineItemsUpdate({
      checkout,
      lineItems
    })

    setCheckout(responseCheckout)
    setCheckoutUpToDate(true)
    setLoading(false)
  }

  const updateLineItemInCart = async (lineItem, quantity, isSubscription) => {
    setLoading(true)

    const item = {
      variantId: lineItem.variant.id,
      quantity
    }

    if (isSubscription) item.customAttributes = [{ key: 'orderType', value: 'subscription' }]

    const lineItems = [item]

    const previousQty = checkout.lineItems.edges.find(e => {
      const idMatch = e.node.id === lineItem.id
      const orderType = item.customAttributes?.find(attr => attr.key === 'orderType')?.value
      const orderTypeMatch = orderType ? orderType === 'subscription' && isSubscription : !isSubscription
      return idMatch && orderTypeMatch
    }).node.quantity

    const responseCheckout = await checkoutLineItemsUpdate({
      checkout,
      lineItems
    })

    setCheckout(responseCheckout)
    setLoading(false)
    setCheckoutUpToDate(true)

    try {
      trackEvent({
        name: quantity < previousQty ? 'cartProdRemove' : 'cartProdAdd',
        time: Date(),
        product: [
          {
            variantId: lineItem.variant.id,
            quantity
          }
        ]
      })

      window.dataObject.cartProduct = responseCheckout.lineItems.edges.map(e => ({
        quantity: e.node.quantity,
        variantId: e.node.variant.id,
        price: e.node.variant.price,
        name: e.node.title + ' ' + e.node.variant.name
      }))

      const orderType = lineItem.customAttributes?.some(
        attr => attr.key === 'orderType' && attr.value === 'subscription'
      )
        ? 'Subscription'
        : 'One-Time'
      const attributes = getVariantAttributes({ variant: lineItem.variant, orderType })
      const splitProductId = lineItem.variant.id.split('__')
      const productId = splitProductId[splitProductId.length - 1]
      const productName = responseCheckout.lineItems.edges.find(e => e.node.variant.sku === lineItem.variant.sku).node
        .title
      window._satellite.track(quantity < previousQty ? 'scRemove' : 'scAdd', {
        productInfo: [
          {
            productId,
            productSku: lineItem.variant.sku,
            productName,
            price: lineItem.variant.price,
            quantity: 1, //only able to add one item at a time here
            attributes
          }
        ]
      })
    } catch (e) {
      console.log(e)
    }

    setLoading(false)
  }

  const removeLineItemsInCart = async ({ itemsToRemove, isSubscriptionCheckoutItems }) => {
    if (!checkout) return
    setLoading(true)
    const responseCheckout = await checkoutLineItemsRemove({
      checkout,
      itemsToRemove
    })

    setCheckout(responseCheckout)
    setLoading(false)

    trackEvent({
      name: 'cartProdDelete',
      time: Date(),
      product: itemsToRemove.map(i => {
        return { variantId: i.variantId, quantity: 0 }
      })
    })

    const removedProducts = itemsToRemove.map(i => {
      const { title, variant } = checkout.lineItems.edges.find(l => l.node.variant.id === i.variantId)?.node
      return {
        name: title,
        id: variant?.sku,
        price: variant?.price,
        brand: 'vype',
        category: '',
        variant: variant?.title
      }
    })

    window.dataLayer.push({
      event: 'removeFromCart',
      ecommerce: {
        currencyCode: 'CAN',
        remove: {
          products: removedProducts
        }
      }
    })
    if (!isSubscriptionCheckoutItems) {
      window._satellite.track('scRemove', {
        productInfo: itemsToRemove.map(i => {
          const li = checkout.lineItems.edges.find(l => l.node.variant.id === i.variantId)?.node
          const orderType = li.customAttributes?.some(attr => attr.key === 'orderType' && attr.value === 'subscription')
            ? 'Subscription'
            : 'One-Time'
          const attributes = getVariantAttributes({ variant: li.variant, orderType })

          return {
            productId: li.variant.id,
            productSku: li.variant.sku,
            productName: li.title,
            price: li.variant.price,
            quantity: li.quantity,
            attributes
          }
        })
      })
    }
  }

  const removeSubscriptionItemsFromCheckout = async () => {
    const itemsToRemove = subscriptionLineItems.map(lineItem => {
      return {
        variantId: lineItem.node.variant.id,
        customAttributes: lineItem.node.customAttributes
      }
    })

    return removeLineItemsInCart({ itemsToRemove, isSubscriptionCheckoutItems: true })
  }

  const showToast = message => {
    setToastVisible(true)
    setToastMessage(message)

    setTimeout(() => {
      setToastVisible(false)
    }, 5000)
  }

  const checkoutUrl = (urlParams = {}) => {
    let url = `/checkout`
    const hasUrlParams = Object.keys(urlParams).length > 0
    if (hasUrlParams) url += `?${Object.keys(urlParams).map(key => `${key}=${urlParams[key]}`)}`
    return url
  }

  const applyDiscountCode = async discountCode => {
    const responseCheckout = await checkoutDiscountCodeApply({
      checkout,
      discountCode
    })

    if (responseCheckout.error) {
      setApplyDiscountCodeError(responseCheckout.error)
    } else {
      setCheckout(responseCheckout)
      await removeCredit()

      try {
        const coupon = checkout.discountApplications.edges.filter(discount => discount.node.code)?.[0]?.node
        digitalData.push({
          cart: {
            cartId: checkout.id,
            price: {
              subtotal: checkout.subtotalPrice,
              cartTotal: checkout.totalPrice,
              coupon: coupon
                ? {
                    couponCode: coupon.code,
                    couponDiscount: coupon.value.percentage || coupon.value.amount
                  }
                : null
            }
          }
        })
      } catch (e) {
        console.log(e)
      }
    }
  }

  const removeDiscountCode = async discount => {
    const responseCheckout = await checkoutDiscountCodeRemove({ checkout })
    setCheckout(responseCheckout)
  }

  const applyCredit = async creditAmount => {
    await removeDiscountCode()

    const responseCheckout = await checkoutUpdateCustomAttribute({
      checkoutId: checkout.id,
      input: {
        customAttributes: [
          ...checkout.customAttributes,
          {
            key: 'creditAmount',
            value: String(creditAmount)
          }
        ]
      }
    })

    setCheckout(responseCheckout)
  }

  const removeCredit = async () => {
    const customAttributes = [...checkout.customAttributes]
    const index = customAttributes.findIndex(a => a.key === 'creditAmount')

    if (index > -1) {
      customAttributes.splice(index, 1)

      const responseCheckout = await checkoutUpdateCustomAttribute({
        checkoutId: checkout.id,
        input: {
          customAttributes
        }
      })

      setCheckout(responseCheckout)
    }
  }

  const clearCheckout = async ({ customAttributes = {} } = {}) => {
    await createCheckout({ customAttributes })
  }

  const clearDraftOrder = async () => {
    await removeSubscriptionItemsFromCheckout()
    setDraftOrder({})
  }

  const value = {
    checkout,
    loading,
    checkoutUpToDate,
    toastVisible,
    toastMessage,
    discount,
    authSdk,
    user,
    priceBreakdown,
    priceBreakdownLoading,
    tinyCartVisible,
    regularLineItems,
    subscriptionLineItems: skipSubscription ? [] : subscriptionLineItems,
    regularLineItemsQuantity,
    subscriptionLineItemsQuantity: skipSubscription ? 0 : subscriptionLineItemsQuantity,
    addLineItemsToCart,
    updateLineItemInCart,
    updateLineItemsInCart,
    removeLineItemsInCart,
    createCheckout,
    checkoutUrl,
    applyDiscountCode,
    removeDiscountCode,
    setAuthSdk,
    setCheckoutUser,
    setPriceBreakdown,
    setTinyCartVisible,
    clearCheckout,
    dispatchPriceBreakdown,
    applyDiscountCodeError,
    applyCredit,
    applyCreditError,
    removeCredit,
    products,
    fetchProducts,
    setSkipsubscription,
    draftOrder,
    queuedOrder,
    setQueuedOrder,
    removeSubscriptionItemsFromCheckout,
    setDraftOrder,
    clearDraftOrder,
    hasSeenChristmasPopup,
    setHasSeenChristmasPopup,
    noAutoDiscount,
    setNoAutoDiscount,
    selectedShippingMethod,
    setSelectedShippingMethod,
    selectedSubscriptionShippingMethod,
    setSelectedSubscriptionShippingMethod
  }

  return (
    <Context.Provider value={value}>
      <Toast visible={toastVisible} message={toastMessage} />
      {children}
    </Context.Provider>
  )
}

CheckoutProvider.propTypes = {
  children: node
}

const { Consumer } = Context
export { Consumer as CheckoutConsumer, CheckoutProvider }

export default Context
